import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import * as Yup from 'yup'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthContext } from 'context/AuthContext'
import PageLoader from 'components/PageLoader'
import { useAlert } from 'context/AlertContext'
import { axiosPost } from 'connectors/axiosPost'
import { AxiosResponse } from 'axios'

type FormFields = {
  message: string
}

type RejectButtonProps = {
  requestId: number
  disabled: boolean
  type: 'prize-embargo' | 'prize-draw'
}

export const RejectButton = ({ requestId, disabled, type }: RejectButtonProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'prizesManagement' })
  const { changeMessage } = useAlert()
  const { token, refreshToken } = useAuthContext()
  const [modalOpen, setModalOpen] = useState(false)
  const queryClient = useQueryClient()

  const { mutate, isLoading } = useMutation(
    async (body: FormFields) =>
      await axiosPost<object | { error: AxiosResponse | undefined }>(
        token,
        refreshToken,
        `${type}/request/reject/${requestId}`,
        body
      ),
    {
      onSuccess: data => {
        if (data && 'error' in data) {
          changeMessage(`Failure during rejecting, ${JSON.stringify(data)}`, 'warning')
        } else {
          changeMessage('Request rejected', 'success')
        }

        queryClient.refetchQueries({ exact: false, queryKey: [`request-${requestId}`] })
      },
    }
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      message: '',
    },
    resolver: yupResolver(
      Yup.object().shape({
        message: Yup.string().required(t('common.required').toString()),
      })
    ),
  })

  const openModal = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const onSubmit = (data: FormFields) => {
    mutate(data)
  }

  return (
    <>
      <Button size="small" variant="outlined" color="error" onClick={openModal} disabled={disabled}>
        {t('actions.reject')}
      </Button>
      {isLoading && <PageLoader />}
      {modalOpen && (
        <Dialog PaperProps={{ sx: { width: '100%', maxWidth: 400, p: 2 } }} open={modalOpen} onClose={closeModal}>
          <DialogTitle sx={{ justifyContent: 'space-between', display: 'flex', alignItems: 'flex-start', pb: 0 }}>
            <Box
              flexShrink={0}
              flexGrow={0}
              width="64px"
              height="64px"
              left={-16}
              top={-12}
              position="relative"
              sx={({ palette }) => ({
                boxShadow: `
                inset 0 0 90px #FEE4E2, 
                0 0 0 16px ${palette.background.paper}, 
                0 0 0 17px ${darken(palette.background.default, 0.05)}, 
                0 0 0 33px ${palette.background.paper}, 
                0 0 0 34px ${darken(palette.background.default, 0.03)}, 
                0 0 0 50px ${palette.background.paper}, 
                0 0 0 51px ${darken(palette.background.default, 0.01)},
                0 0 0 67px ${palette.background.paper}, 
                0 0 0 68px ${palette.background.default}`,
                border: `8px solid #FEE4E245;`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              })}
            >
              <WarningAmberRounded fontSize="large" color="error" />
            </Box>
            <IconButton onClick={closeModal}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 1, pb: 4, zIndex: 2 }}>
            <Typography fontWeight={600} textAlign="center" variant="h5" mb={1}>
              {t('application.rejestingTitle')}
            </Typography>
            <Typography textAlign="center" mb={2}>
              {t('application.rejectingDesc')}
            </Typography>
            <form id="reason-form" onSubmit={handleSubmit(onSubmit)}>
              <FormControl fullWidth error={Boolean(errors?.message?.message)}>
                <TextField {...register('message')} />
                {errors?.message?.message !== undefined ? (
                  <FormHelperText>
                    <>{errors?.message?.message}</>
                  </FormHelperText>
                ) : null}
              </FormControl>
            </form>
          </DialogContent>
          <DialogActions>
            <Button fullWidth type="button" color="secondary" variant="outlined" onClick={closeModal}>
              {t('actions.cancel')}
            </Button>
            <Button fullWidth type="submit" variant="contained" form="reason-form">
              {t('actions.reject')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
